import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { BackgroundProcess, FileData } from 'src/data-capture/hooks/file-uploads/types';

type SuitabilityReportType = 'NO_CHANGE';

interface BaseDocument {
  id: string;
  clientId?: string;
  familyId?: string;
  createdAt: string;
  fileId?: number;
  file?: FileData;
  backgroundProcess?: BackgroundProcess;
}

interface SuitabilityReportDocument extends BaseDocument {
  documentType: 'suitability-report';
  reportType?: SuitabilityReportType;
}

interface MeetingAgendaDocument extends BaseDocument {
  documentType: 'meeting-agenda';
  documentHistoryId?: string;
}

interface DataExportDocument extends BaseDocument {
  documentType: 'data-export';
  configDisplayName?: string;
  documentHistoryId?: string;
}

interface MeetingNoteDocument extends BaseDocument {
  documentType: 'meeting-note';
  dataUploadFileId?: string;
  documentHistoryId?: string;
  generatedFromAgenda?: boolean;
}

interface DataUploadDocument extends BaseDocument {
  documentType: 'data-upload';
  fileName: string;
  dataUploadModelId?: string;
}

export type UnifiedDocument =
  | SuitabilityReportDocument
  | MeetingAgendaDocument
  | DataExportDocument
  | MeetingNoteDocument
  | DataUploadDocument;

type UseAllClientDocumentsOptions = (
  | {
      type?: 'client';
      clientId?: string;
    }
  | {
      type: 'family';
      familyId: string;
    }
) & {
  // Poll while a document is generating (and briefly on mount, to catch a just-created one).
  // Opt-in: only pages that trigger generation and want live updates need it.
  poll?: boolean;
};

type UseAllClientDocumentsReturn = {
  documents: UnifiedDocument[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
};

export const useAllClientDocuments = (
  options: UseAllClientDocumentsOptions = { type: 'client' },
): UseAllClientDocumentsReturn => {
  const { clientId: defaultClientId } = useCurrentClientIfAvailable();

  const queryKey =
    options.type === 'family'
      ? ['families', options.familyId, 'all-documents']
      : ['clients', options.clientId ?? defaultClientId, 'all-documents'];

  const enabled =
    options.type === 'family' ? !!options.familyId : !!(options.clientId ?? defaultClientId);

  const entityKey =
    options.type === 'family' ? options.familyId : (options.clientId ?? defaultClientId);

  // Mount time for the initial "wait for a just-created document" polling window.
  // Reset whenever the entity changes so a newly-opened entity gets its own window.
  const firstRequestTimeRef = useRef<number>(Date.now());
  useEffect(() => {
    firstRequestTimeRef.current = Date.now();
  }, [entityKey]);

  const { data, isLoading, isError, error, refetch } = useQuery<UnifiedDocument[], Error>({
    queryKey,
    enabled,
    staleTime: 30000,
    refetchOnWindowFocus: false,
    // Function form is re-evaluated after every fetch, so polling reliably stops
    // once nothing is processing and the initial window has elapsed (a state+effect
    // would get stuck when react-query returns a stable, unchanged data reference).
    refetchInterval: options.poll
      ? query => {
          const docs = query.state.data ?? [];
          const isProcessing = docs.some(
            doc =>
              doc.backgroundProcess &&
              ['PROCESSING', 'PENDING'].includes(doc.backgroundProcess.status),
          );
          const withinInitialWindow =
            (Date.now() - firstRequestTimeRef.current) / 1000 < 30 && docs.length === 0;
          return isProcessing || withinInitialWindow ? 5000 : false;
        }
      : false,
  });

  const documents = data || [];

  return {
    documents,
    isLoading,
    isError,
    error,
    refetch,
  };
};
